import { Link } from 'react-router-dom';

interface SolutionCardsProps {
  cryptoRevenue: number;
  stripeRevenue: number;
  squareRevenue: number;
  todayCryptoCount: number;
  todayStripeCount: number;
  todaySquareCount: number;
  cryptoOrdersCount: number;
  stripeOrdersCount: number;
  squareOrdersCount: number;
  stripeStatus: string;
  squareStatus: string;
  cryptoPendingCount?: number;
  stripePendingCount?: number;
  squarePendingCount?: number;
}

function PendingBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border text-amber-400 bg-amber-400/10 border-amber-400/20">
      {count} pending
    </span>
  );
}

export default function SolutionCards({
  cryptoRevenue,
  stripeRevenue,
  squareRevenue,
  todayCryptoCount,
  todayStripeCount,
  todaySquareCount,
  cryptoOrdersCount,
  stripeOrdersCount,
  squareOrdersCount,
  stripeStatus,
  squareStatus,
  cryptoPendingCount = 0,
  stripePendingCount = 0,
  squarePendingCount = 0,
}: SolutionCardsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
      <Link
        to="/dashboard/crypto"
        className="rounded p-4 transition-colors group"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-card)'; }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <span className="text-emerald-400 text-sm">{'\u25C7'}</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Crypto</div>
                <PendingBadge count={cryptoPendingCount} />
              </div>
              <div className="font-mono text-base" style={{ color: 'var(--dash-text-primary)' }}>${cryptoRevenue.toFixed(2)}</div>
            </div>
          </div>
          <span className="transition-colors text-sm" style={{ color: 'var(--dash-text-faint)' }}>{'\u2192'}</span>
        </div>
        <div className="flex items-center gap-5">
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Today </span>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-tertiary)' }}>{todayCryptoCount} orders</span>
          </div>
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Total </span>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-tertiary)' }}>{cryptoOrdersCount} orders</span>
          </div>
        </div>
      </Link>

      <Link
        to="/dashboard/stripe"
        className="rounded p-4 transition-colors group"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-card)'; }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <span className="text-blue-400 text-sm">{'\u22A1'}</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Stripe</div>
                <PendingBadge count={stripePendingCount} />
              </div>
              <div className="font-mono text-base" style={{ color: 'var(--dash-text-primary)' }}>${stripeRevenue.toFixed(2)}</div>
            </div>
          </div>
          <span className="transition-colors text-sm" style={{ color: 'var(--dash-text-faint)' }}>{'\u2192'}</span>
        </div>
        <div className="flex items-center gap-5">
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Status </span>
            <span className={`font-mono text-[11px] ${
              stripeStatus === 'Active' ? 'text-emerald-400' :
              stripeStatus === 'Pending' ? 'text-amber-400' : ''
            }`} style={stripeStatus !== 'Active' && stripeStatus !== 'Pending' ? { color: 'var(--dash-text-muted)' } : undefined}>{stripeStatus}</span>
          </div>
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Today </span>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-tertiary)' }}>{todayStripeCount} orders</span>
          </div>
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Total </span>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-tertiary)' }}>{stripeOrdersCount} orders</span>
          </div>
        </div>
      </Link>

      <Link
        to="/dashboard/square"
        className="rounded p-4 transition-colors group"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-card)'; }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
              <span className="text-sky-400 text-sm">{'\u25A0'}</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Square</div>
                <PendingBadge count={squarePendingCount} />
              </div>
              <div className="font-mono text-base" style={{ color: 'var(--dash-text-primary)' }}>${squareRevenue.toFixed(2)}</div>
            </div>
          </div>
          <span className="transition-colors text-sm" style={{ color: 'var(--dash-text-faint)' }}>{'\u2192'}</span>
        </div>
        <div className="flex items-center gap-5">
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Status </span>
            <span className={`font-mono text-[11px] ${
              squareStatus === 'Active' ? 'text-emerald-400' :
              squareStatus === 'Pending' ? 'text-amber-400' : ''
            }`} style={squareStatus !== 'Active' && squareStatus !== 'Pending' ? { color: 'var(--dash-text-muted)' } : undefined}>{squareStatus}</span>
          </div>
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Today </span>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-tertiary)' }}>{todaySquareCount} orders</span>
          </div>
          <div>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>Total </span>
            <span className="font-mono text-[11px]" style={{ color: 'var(--dash-text-tertiary)' }}>{squareOrdersCount} orders</span>
          </div>
        </div>
      </Link>
    </div>
  );
}
